<script lang="ts">
export default {
  name: 'FoodMenuMultiServicePreviewModal',
}
</script>
<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, ref } from 'vue'
import { KkModal, KkButton, KkBox } from '../../../../components/base'
import { KkTabList } from '../../../../components/ui'
import { KkSection, KkItem } from '../../../../domains/foodMenu'
import type {MenuCategory, ServiceType} from 'src/biz_spa/domains/foodMenu/consts'
import {menuCategories} from 'src/biz_spa/domains/foodMenu/consts'
import type {Section} from 'src/biz_spa/domains/foodMenu/types'
import { camelCase } from '../../../../modules/string_converter'
const { t } = useI18n()

interface Props {
  isVisible: boolean
  sections: Section[]
  storeNames: string[]
  selectedServices: ServiceType[]
  isPreview: boolean
  title: string
}

const props = withDefaults(defineProps<Props>(), {
  isVisible: false,
  storeNames: () => [],
  isPreview: false,
  title: ''
})

interface Emits {
  (e: 'close'): void,
  (e: 'submit'): void,
}
const emit = defineEmits<Emits>()

const selectedService = ref<ServiceType>(props.selectedServices[0])
const clickMenu = (key: ServiceType): void => {
  selectedService.value = key
}

const tabMenuList = computed(() => {
  return props.selectedServices
    .reduce<Record<ServiceType, string>>((acc, key) => ({ ...acc, [key]: t(`labels.sites.${camelCase(key)}`) }), {} as Record<ServiceType, string>)
})

const categorySections = (category: MenuCategory) => {
  return props.sections
    .filter(v => v.category === category)
}
const yahooPlaceCategorySections = (category: MenuCategory) => {
  return yahooPlaceCategoryItems.value
    .filter(v => v.category === category)
}
const yahooPlaceItemLabelMaxLength = computed(() => 20)
const yahooPlaceCategoryItems = computed(() => {
  let itemCount = 0
  const itemMaxCount = 50
  return props.sections
    .map(section => ({
      ...section,
      items: section.items.map(item => {
        itemCount++
        if (itemCount > itemMaxCount) return null
        return {
          ...item,
          label: item.label.slice(0, yahooPlaceItemLabelMaxLength.value),
        }
      }).filter((v): v is NonNullable<typeof v> => v != null)
    }))
})

const categories = menuCategories
const filterCategories = computed(() => {
  switch (selectedService.value) {
  case 'tabelog':
    return categories.filter(v => v !== 'takeout')
  case 'yahoo_place':
    return categories.filter(v => !['lunch', 'takeout'].includes(v))
  default:
    return categories
  }
})
</script>

<template>
  <KkModal :is-visible="props.isVisible" width="1076px" @change="emit('close')">
    <div class="p-modal__content__header">
      <div class="title">
        {{ props.title }}
        <div v-if="!props.isPreview">
          ( <span class="storeCount">{{ t('pages.CompaniesFoodMenus.edit.preview.storesCount', {count : props.storeNames.length}) }}</span>)
        </div>
      </div>
    </div>
    <div class="content__tab">
      <KkTabList
        :menu-list="tabMenuList"
        :selected-tab-key="selectedService"
        @click-menu="clickMenu($event as ServiceType)"
      />
    </div>
    <div class="p-modal__content__body">
      <div v-if="selectedService === 'google'">
        <template v-for="(category, categoryIndex) in filterCategories" :key="categoryIndex">
          <div v-for="(section, sectionIndex) in categorySections(category)" :key="sectionIndex" class="section">
            <KkSection :selected-service="selectedService" :section="section" />
          </div>
        </template>
      </div>
      <div v-else-if="selectedService === 'retty'">
        <template v-for="(category, categoryIndex) in filterCategories" :key="categoryIndex">
          <div v-if="categorySections(category).length !== 0" class="category">
            <KkBox :title="t(`pages.CompaniesFoodMenus.edit.category.${category}`)" content-height="3000px" border>
              <template #content>
                <template v-for="(section, sectionIndex) in categorySections(category)" :key="sectionIndex">
                  <template v-for="(item, itemIndex) in section.items" :key="itemIndex">
                    <KkItem :selected-service="selectedService" :item="item" />
                  </template>
                </template>
              </template>
            </KkBox>
          </div>
        </template>
      </div>
      <div v-else-if="selectedService === 'yahoo_place'">
        <template v-for="(category, categoryIndex) in filterCategories" :key="categoryIndex">
          <div v-if="yahooPlaceCategorySections(category).length !== 0" class="category">
            <KkBox :title="t(`pages.CompaniesFoodMenus.edit.category.${category}`)" content-height="3000px" border>
              <template #content>
                <template v-for="(section, sectionIndex) in yahooPlaceCategorySections(category)" :key="sectionIndex">
                  <template v-for="(item, itemIndex) in section.items" :key="itemIndex">
                    <KkItem :selected-service="selectedService" :item="item" />
                  </template>
                </template>
              </template>
            </KkBox>
          </div>
        </template>
      </div>
      <div v-else>
        <template v-for="(category, categoryIndex) in filterCategories" :key="categoryIndex">
          <div v-if="categorySections(category).length !== 0" class="category">
            <KkBox :title="t(`pages.CompaniesFoodMenus.edit.category.${category}`)" content-height="3000px" border>
              <template #content>
                <div v-for="(section, sectionIndex) in categorySections(category)" :key="sectionIndex" class="section">
                  <KkSection :selected-service="selectedService" :section="section" />
                </div>
              </template>
            </KkBox>
          </div>
        </template>
      </div>
      <div v-if="!isPreview && storeNames.length" class="selectedStores">
        <p class="selectedStores__title"> {{ t('pages.CompaniesFoodMenus.edit.preview.selectedStores') }} </p>
        <div class="p-table">
          <div class="p-table__content">
            <table class="selectedStores__table">
              <thead>
                <tr>
                  <th />
                  <th>{{ t('pages.CompaniesFoodMenus.edit.preview.storeName') }}</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="(storeName, index) in props.storeNames" :key="index">
                  <tr>
                    <td class="selectedStores__table__index">
                      {{ index + 1 }}
                    </td>
                    <td>
                      {{ storeName }}
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    <div class="p-modal__content__footer">
      <div class="c-button c-button--right">
        <KkButton
          class="footerCancelBtn"
          color="secondary"
          outlined
          :text="t('labels.cancel')"
          @click="emit('close')"
        />
        <KkButton
          v-if="!props.isPreview"
          color="primary"
          outlined
          :text="t('labels.confirm')"
          @click="emit('submit')"
        />
      </div>
    </div>
  </KkModal>
</template>

<style lang="scss" scoped>
@import '../../../../../../stylesheets/biz_spa/foundation/_var.scss';

.title {
  display: flex;
  font-weight: normal;
}
.storeCount {
  font-weight: bold;
}
.protectionIcon {
  width: 12px;
  margin-left: 5px;
}
.footerCancelBtn {
  margin-right: 8px;
}
.category:not(:last-child) {
  margin-bottom: 32px;
}
.section:not(:last-child) {
  margin-bottom: 32px;
}
.p-table {
  margin-top: 12px;
}

.selectedStores {
  margin-top: 32px;
  &__title {
    font-size: 12px;
    font-weight: bold;
  }
  &__table {
    border-collapse: separate;
    border-spacing: 0;
    thead::after {
      content: '';
      display: block;
      height: 10px;
    }

    &__index {
      width: 10%;
      background-color: #f8f9fa;
    }
  }
}
</style>
